/**
 * Copy-trading contract interaction helpers.
 *
 * Follower actions: follow, unfollow
 * Trader actions:   registerAsTrader, updateTraderSettings, claimCopyFees, toggleCopyPause
 *
 * All functions use callContract() so the user gets standard toast + pending-tx tracking.
 */
import { t } from "@lingui/macro";
import { ethers } from "ethers";

import { getContract } from "config/contracts";
import { callContract } from "lib/contracts/callContract";

import CopyTradingStorageArtifact from "sdk/abis/CopyTradingStorage.json";
import RouterArtifact from "sdk/abis/Router.json";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStorageContract(chainId: number, signer: ethers.Signer) {
  const addr = getContract(chainId, "CopyTradingStorage");
  const abi = Array.isArray(CopyTradingStorageArtifact) ? CopyTradingStorageArtifact : (CopyTradingStorageArtifact as any).abi;
  return new ethers.Contract(addr, abi, signer);
}

function getRouterContract(chainId: number, signer: ethers.Signer) {
  const addr = getContract(chainId, "Router");
  return new ethers.Contract(addr, RouterArtifact.abi, signer);
}

function getSynthRouterContract(chainId: number, signer: ethers.Signer) {
  const addr = getContract(chainId, "SynthRouter");
  return new ethers.Contract(addr, RouterArtifact.abi, signer);
}

// ─── Follower: follow ─────────────────────────────────────────────────────────

export type FollowParams = {
  chainId: number;
  signer: ethers.Signer;
  traderAddress: string;
  collateralPerTrade: bigint;
  maxTotalAllocation: bigint;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function followTrader({
  chainId,
  signer,
  traderAddress,
  collateralPerTrade,
  maxTotalAllocation,
  setPendingTxns,
}: FollowParams): Promise<void> {
  const managerAddress = getContract(chainId, "CopyTradingManager");
  const router = getRouterContract(chainId, signer);
  const synthRouter = getSynthRouterContract(chainId, signer);
  const storage = getStorageContract(chainId, signer);

  const followerAddress = await signer.getAddress();

  // Approve manager on main Router (native markets)
  const isMainApproved: boolean = await router.approvedPlugins(followerAddress, managerAddress).catch(() => false);
  if (!isMainApproved) {
    await callContract(chainId, router, "approvePlugin", [managerAddress], {
      sentMsg: t`Approving Copy Trading (main markets)…`,
      successMsg: t`Copy Trading approved for native markets.`,
      setPendingTxns,
    });
  }

  // Approve manager on SynthRouter (synthetic markets — crypto + stocks)
  const isSynthApproved: boolean = await synthRouter.approvedPlugins(followerAddress, managerAddress).catch(() => false);
  if (!isSynthApproved) {
    await callContract(chainId, synthRouter, "approvePlugin", [managerAddress], {
      sentMsg: t`Approving Copy Trading (synthetic markets)…`,
      successMsg: t`Copy Trading approved for synthetic markets.`,
      setPendingTxns,
    });
  }

  await callContract(chainId, storage, "follow", [traderAddress, collateralPerTrade, maxTotalAllocation], {
    sentMsg:    t`Following trader…`,
    successMsg: t`Now following trader.`,
    failMsg:    t`Failed to follow trader.`,
    setPendingTxns,
  });
}

// ─── Follower: unfollow ───────────────────────────────────────────────────────

export type UnfollowParams = {
  chainId: number;
  signer: ethers.Signer;
  traderAddress: string;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function unfollowTrader({ chainId, signer, traderAddress, setPendingTxns }: UnfollowParams) {
  const storage = getStorageContract(chainId, signer);
  await callContract(chainId, storage, "unfollow", [traderAddress], {
    sentMsg:    t`Unfollowing trader…`,
    successMsg: t`Unfollowed successfully.`,
    failMsg:    t`Failed to unfollow trader.`,
    setPendingTxns,
  });
}

// ─── Trader: register ─────────────────────────────────────────────────────────

export type RegisterTraderParams = {
  chainId: number;
  signer: ethers.Signer;
  /** Copy fee in basis points (0–1000 = 0–10%) */
  copyFeeRateBps: number;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function registerAsTrader({ chainId, signer, copyFeeRateBps, setPendingTxns }: RegisterTraderParams) {
  const storage = getStorageContract(chainId, signer);
  await callContract(chainId, storage, "registerAsTrader", [copyFeeRateBps], {
    sentMsg:    t`Registering as a leader trader…`,
    successMsg: t`You are now a registered leader. Followers can copy your trades.`,
    failMsg:    t`Registration failed.`,
    setPendingTxns,
  });
}

// ─── Trader: update settings ──────────────────────────────────────────────────

export type UpdateTraderSettingsParams = {
  chainId: number;
  signer: ethers.Signer;
  copyFeeRateBps: number;
  maxFollowers: number;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function updateTraderSettings({
  chainId, signer, copyFeeRateBps, maxFollowers, setPendingTxns,
}: UpdateTraderSettingsParams) {
  const storage = getStorageContract(chainId, signer);
  await callContract(chainId, storage, "updateTraderSettings", [copyFeeRateBps, maxFollowers], {
    sentMsg:    t`Updating trader settings…`,
    successMsg: t`Settings updated.`,
    failMsg:    t`Failed to update settings.`,
    setPendingTxns,
  });
}

// ─── Trader: claim fees ───────────────────────────────────────────────────────

export type ClaimCopyFeesParams = {
  chainId: number;
  signer: ethers.Signer;
  /** Token address to claim (e.g. USDT or USDC) */
  tokenAddress: string;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function claimCopyFees({ chainId, signer, tokenAddress, setPendingTxns }: ClaimCopyFeesParams) {
  const storage = getStorageContract(chainId, signer);
  await callContract(chainId, storage, "claimFees", [tokenAddress], {
    sentMsg:    t`Claiming copy trading fees…`,
    successMsg: t`Fees claimed successfully.`,
    failMsg:    t`Failed to claim fees.`,
    setPendingTxns,
  });
}

// ─── Follower: toggle pause ───────────────────────────────────────────────────

export type TogglePauseParams = {
  chainId: number;
  signer: ethers.Signer;
  traderAddress: string;
  setPendingTxns: (fn: (prev: any[]) => any[]) => void;
};

export async function toggleCopyPause({ chainId, signer, traderAddress, setPendingTxns }: TogglePauseParams) {
  const storage = getStorageContract(chainId, signer);
  await callContract(chainId, storage, "togglePause", [traderAddress], {
    sentMsg:    t`Updating pause state…`,
    successMsg: t`Copy trading pause toggled.`,
    failMsg:    t`Failed to toggle pause.`,
    setPendingTxns,
  });
}
